import i18next from "src/i18n/config";
import RouteService from "src/services/routeService";
import { invalidateOnSuccess, transformErrorResponse } from "src/store/helpers";
import { ApiResponse, PaginatedResponse } from "src/types";

import { toBackendPageIndex } from "@shared/table/Tanstack.utils";

import { AdminBuilder } from "./tags";

export const announcementEmailListTypes = {
    newsletterSubscribers: "NewsletterSubscribers",
} as const;

type AnnouncementEmailListType = (typeof announcementEmailListTypes)[keyof typeof announcementEmailListTypes];

export type ExportAnnouncementEmailsPayload = {
    listType?: AnnouncementEmailListType;
};

const getExportFileName = (listType?: AnnouncementEmailListType) =>
    listType === announcementEmailListTypes.newsletterSubscribers ? "subscribers.csv" : "customers.csv";

export type AnnouncementSubscriber = {
    mail: string;
    source: string;
};

type GetSubscribersParams = {
    email?: string;
    pageIndex?: number;
    pageSize?: number;
    sortCriteria?: "mail" | "source";
    sortDirection?: 0 | 1;
};

type UnsubscribeParams = {
    emails: string[];
    reason?: string;
};

type UnsubscribeResult = {
    removed: string[];
    unknown: string[];
};

const announcements = (builder: AdminBuilder) => ({
    getSubscribers: builder.query<PaginatedResponse<AnnouncementSubscriber>, GetSubscribersParams>({
        providesTags: ["Subscribers"],
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        query: async (payload) => {
            const base = await RouteService.getAnnouncementsRoute();
            return {
                url: `${base}subscribers`,
                params: { ...payload, pageIndex: toBackendPageIndex(payload.pageIndex) },
            };
        },
    }),

    exportAnnouncementEmails: builder.mutation<{ blob: Blob; fileName: string }, ExportAnnouncementEmailsPayload>({
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (blob: Blob, _meta, arg) => ({ blob, fileName: getExportFileName(arg.listType) }),
        query: async ({ listType }) => {
            const base = await RouteService.getAnnouncementsRoute();
            return {
                url: `${base}emails.csv`,
                responseType: "blob",
                params: listType ? { listType } : undefined,
            };
        },
    }),

    unsubscribeSubscribers: builder.mutation<UnsubscribeResult, UnsubscribeParams>({
        invalidatesTags: invalidateOnSuccess("Subscribers"),
        transformErrorResponse: (err) => transformErrorResponse(err, i18next.t("common:error.technicalDifficulties.message")),
        transformResponse: (response: ApiResponse<UnsubscribeResult>) => ({
            removed: response.model?.removed ?? [],
            unknown: response.model?.unknown ?? [],
        }),
        query: async ({ emails, reason }) => {
            const base = await RouteService.getAnnouncementsRoute();
            const trimmedReason = reason?.trim();
            return {
                url: `${base}unsubscribe`,
                method: "POST",
                data: { Emails: emails, ...(trimmedReason ? { Reason: trimmedReason } : {}) },
                headers: { "Content-Type": "application/json" },
            };
        },
    }),
});

export default announcements;
